// ./src/answerImageFields.ts — ROADMAP A2e #91: the picture fields, and a read that survives a gateway that predates them.
//
// A picture question (`imageShape`), a picture answer (`valueImage`) and a comment's author picture (`commenterPhoto`,
// `commenterAvatarUrl`) are new output fields on reads the public site cannot do without — the RSVP form, the ticket form,
// the sponsorship and volunteer forms, "my RSVP", every comment thread. A gateway that has not been restarted since the
// API shipped them refuses the WHOLE query, which would take every form and guest book off the site. So each such read
// is built twice (`imageSafeDocs`) and retried without the fields when — and only when — the gateway names one of them.
// ⓘ #136 rides the same mechanism: a guest's `checkInCode` (on "my RSVP" and on the RSVP reply itself — a refused reply
//   would stop every guest replying, not only lose their QR).
import { gql } from "@apollo/client";
import type { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import type { AnswerImageInput, AnswerImageValue } from "./PublicRsvpService";
import { errorMessages } from "./opResultHelpers";

/** On a question: which cropper to offer (avatar | logo). */
export const QUESTION_IMAGE_FIELDS = "imageShape";

/** On an answer: the picture, a token to send it back unchanged, and its URL. */
export const ANSWER_IMAGE_FIELDS = "valueImage { uploadedFileId token uploadedFile { id fileUrl thumbnailUrl } }";

/** On a comment: the author's picture (a stored photo, else a ready URL). */
export const COMMENT_AVATAR_FIELDS = "commenterPhoto { id fileUrl thumbnailUrl } commenterAvatarUrl";

/** On a guest: the code the server lets them show as their check-in QR (#136). Always selected on a line of its own. */
export const CHECK_IN_CODE_FIELD = "checkInCode";

/**
 * #149 — on a guest's own sign-up: paid for online. Rides this retry because it is on "my RSVP", whose refusal would
 * un-recognise the guest on every block. ⓘ The GraphQL comment makes the selection unique, so only THIS one is stripped.
 */
export const SIGNUP_PAID_FIELD = "paid # #149 signup paid online";

/**
 * #150 — on a pay link: the payment arrived after the place was released and was refunded. Rides this retry because a
 * refused pay-link read shows "no longer valid" to somebody holding a real link. ⓘ Unique by its comment, like `paid`.
 */
export const PAY_LINK_REFUNDED_FIELD = "refunded # #150 pay link refunded";

/** The same query source without any picture field (nor the check-in code, which only ever stands on its own line). */
export const withoutAnswerImageFields = (src: string): string =>
  src
    .split(SIGNUP_PAID_FIELD)
    .join("")
    .split(PAY_LINK_REFUNDED_FIELD)
    .join("")
    .split(ANSWER_IMAGE_FIELDS)
    .join("")
    .split(COMMENT_AVATAR_FIELDS)
    .join("")
    .split(QUESTION_IMAGE_FIELDS)
    .join("")
    .replace(new RegExp(`^\\s*${CHECK_IN_CODE_FIELD}\\s*$`, "gm"), "");

/** The gateway refused the query because it does not know a picture field (or the check-in code) yet — nothing else. */
export const refusedAnswerImageFields = (result: OpResult): boolean =>
  !result.didSucceed() &&
  /Cannot query field ["'](imageShape|valueImage|commenterPhoto|commenterAvatarUrl|checkInCode)["']|Cannot query field ["']paid["'] on type ["']PublicRsvpMineSignup["']|Cannot query field ["']refunded["'] on type ["']PublicTicketPayLink["']/.test(
    errorMessages(result).join("\n")
  );

export interface ImageSafeDocs {
  full: ReturnType<typeof gql>;
  base: ReturnType<typeof gql>;
}

/** A query (or mutation) source → the document with the picture fields, and the one without. */
export const imageSafeDocs = (src: string): ImageSafeDocs => ({ full: gql(src), base: gql(withoutAnswerImageFields(src)) });

/** Run a read; when the gateway refuses a picture field, run it again without them. */
export const executeQueryImageSafe = async (docs: ImageSafeDocs, variables: Record<string, any>, options: Record<string, any> = {}): Promise<OpResult> => {
  const result = await ApiService.executeQuery(docs.full, variables, options);

  return refusedAnswerImageFields(result) ? ApiService.executeQuery(docs.base, variables, options) : result;
};

/**
 * Run a write that RETURNS picture fields (a comment post / edit). ⚠ Retried only when the gateway refused the
 * selection — validation happens before execution, so the refused write never ran and sending it again cannot double it.
 */
export const executeMutationImageSafe = async (docs: ImageSafeDocs, variables: Record<string, any>, options: Record<string, any> = {}): Promise<OpResult> => {
  const result = await ApiService.executeMutation(docs.full, variables, options);

  return refusedAnswerImageFields(result) ? ApiService.executeMutation(docs.base, variables, options) : result;
};

/** A form value that is a picture answer (a file the server handed back) — the one test both QuestionInput copies use. */
export const isAnswerImage = (v: unknown): v is AnswerImageValue =>
  !!v && typeof v === "object" && !Array.isArray(v) && typeof (v as AnswerImageValue).uploadedFileId === "string" && !!(v as AnswerImageValue).uploadedFileId;

/** The picture answer as the API takes it back: the file and its token, nothing else. */
export const toAnswerImageInput = (v: AnswerImageValue): AnswerImageInput => ({ uploadedFileId: v.uploadedFileId, token: v.token });
